import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Guard a modal against being closed while its form has unsaved input.
 *
 * On `open`, an initial JSON snapshot of `currentValue` is captured. `isDirty`
 * becomes true whenever the current value's JSON no longer matches the snapshot.
 *
 * Call `requestClose()` from close/backdrop/cancel handlers instead of `onClose`
 * — it opens the discard dialog when dirty, or closes immediately when clean.
 *
 * `resetBaseline()` re-snapshots the current value (call after a successful save
 * so leftover state doesn't count as dirty).
 */
export function useUnsavedChangesGuard<T>(
  open: boolean,
  currentValue: T,
  onClose: () => void,
) {
  const [discardOpen, setDiscardOpen] = useState(false);
  const initialSnapshotRef = useRef<string>('');

  // Capture / clear the baseline snapshot whenever the modal opens or closes.
  useEffect(() => {
    if (open) {
      initialSnapshotRef.current = JSON.stringify(currentValue);
    } else {
      initialSnapshotRef.current = '';
      setDiscardOpen(false);
    }
    // We only re-snapshot on transitions of `open`, not on every value change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const currentSnapshot = useMemo(() => JSON.stringify(currentValue), [currentValue]);
  const isDirty = initialSnapshotRef.current !== '' && currentSnapshot !== initialSnapshotRef.current;

  const requestClose = useCallback(() => {
    if (isDirty) { setDiscardOpen(true); return; }
    onClose();
  }, [isDirty, onClose]);

  const confirmDiscard = useCallback(() => {
    setDiscardOpen(false);
    onClose();
  }, [onClose]);

  const cancelDiscard = useCallback(() => setDiscardOpen(false), []);

  const resetBaseline = useCallback(() => {
    initialSnapshotRef.current = JSON.stringify(currentValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue]);

  return { isDirty, discardOpen, requestClose, confirmDiscard, cancelDiscard, resetBaseline };
}
